import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import Section from '../common/Section';
import Button from '../common/Button';
import { DataContext } from '../../context/DataProvider';
import { motion } from 'framer-motion';
import { ArrowRight, BookOpen, Clock, User, Sparkles } from 'lucide-react';

const LatestBlogSection = () => {
    const { blogs, loading } = useContext(DataContext);

    // Get latest 3 published blog posts
    const latestPosts = blogs.filter(post => post.is_published !== false).slice(0, 3);

    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (e) {
            return dateString;
        }
    };

    // If no articles exist yet, hide section cleanly or show teaser
    if (!loading && latestPosts.length === 0) {
        return null;
    }

    return (
        <Section className="bg-gray-50 py-24 border-t border-gray-200">
            <div className="container mx-auto px-4">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
                    <div>
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#bebd19]/20 text-gray-900 text-xs font-bold uppercase tracking-wider mb-4">
                            <Sparkles size={14} className="text-[#bebd19]" />
                            <span>Knowledge & Insights</span>
                        </div>
                        <h2 className="text-3xl md:text-5xl font-bold text-gray-900 tracking-tight">
                            From Our Blog
                        </h2>
                    </div>

                    <Link
                        to="/blog"
                        className="inline-flex items-center gap-2 font-bold text-gray-900 hover:text-[#bebd19] transition-colors text-base group"
                    >
                        View All Articles <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                    </Link>
                </div>

                {/* Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {latestPosts.map((post, index) => (
                        <motion.article
                            key={post.id || index}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: index * 0.1 }}
                            className="group bg-white rounded-2xl border border-gray-200 overflow-hidden hover:border-[#bebd19] hover:shadow-lg transition-all duration-300 flex flex-col"
                        >
                            {/* Image */}
                            <Link to={`/blog/${post.slug}`} className="block h-48 bg-gray-100 overflow-hidden relative">
                                {post.cover_image ? (
                                    <img
                                        src={post.cover_image}
                                        alt={post.title}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-200">
                                        <BookOpen className="w-10 h-10 text-gray-300 group-hover:text-[#bebd19] transition-colors" />
                                    </div>
                                )}
                                {post.category && (
                                    <span className="absolute top-3 left-3 px-3 py-1 bg-white/90 backdrop-blur-md rounded-full text-xs font-bold text-gray-900 border border-gray-200">
                                        {post.category}
                                    </span>
                                )}
                            </Link>

                            <div className="p-6 flex flex-col flex-grow">
                                <Link to={`/blog/${post.slug}`} className="block group-hover:text-[#bebd19] transition-colors">
                                    <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 leading-snug">
                                        {post.title}
                                    </h3>
                                </Link>

                                {post.excerpt && (
                                    <p className="text-gray-500 text-sm mb-6 line-clamp-2 flex-grow leading-relaxed">
                                        {post.excerpt}
                                    </p>
                                )}

                                <div className="flex items-center justify-between text-xs text-gray-500 pt-4 border-t border-gray-100 mt-auto">
                                    <span className="flex items-center gap-1.5 font-medium text-gray-700">
                                        <User size={14} className="text-[#bebd19]" /> {post.author || 'PyrusMedia'}
                                    </span>
                                    {post.created_at && <span>{formatDate(post.created_at)}</span>}
                                </div>
                            </div>
                        </motion.article>
                    ))}
                </div>
            </div>
        </Section>
    );
};

export default LatestBlogSection;
